import { useMemo } from 'react';

import { Box, Divider, Paper, Skeleton, Typography } from '@mui/material';

import { useFactsheetMapImage } from '@/features/Factsheet/api/queries';
import type { FactsheetDetails } from '@/features/Factsheet/types';
import {
  formatFactsheetDate,
  formatFactsheetDateWithFallback
} from '@/features/Factsheet/utils/dateFormatting';
import useUserDateFormat from '@/hooks/useUserDateFormat';

/**
 * Inserts zero-width spaces after common break characters to allow
 * long text to wrap gracefully without breaking layout.
 */
const insertSoftBreaks = (text: string): string => text.replaceAll(/([ _-])/g, '$1\u200B');

interface MapImageProps {
  location: string;
  zoom: string;
}

const MapImage = ({ location, zoom }: MapImageProps) => {
  const { data: imageSrc, isError } = useFactsheetMapImage(location, zoom);

  if (isError) {
    return null;
  }

  return (
    <Box sx={{ width: '40%' }}>
      {imageSrc ? (
        <img
          alt='factsheetImage'
          src={imageSrc}
          style={{ aspectRatio: 1, width: '100%' }}
        />
      ) : (
        <Skeleton
          sx={{ aspectRatio: 1, width: '100%', height: 'auto' }}
          variant='rectangular'
        />
      )}
    </Box>
  );
};

interface TileProps {
  label: string;
  value: string | boolean | string[] | undefined;
}

// eslint-disable-next-line sonarjs/function-return-type
const Tile = ({ label, value }: TileProps) => {
  if (!value) return null;

  const renderValue = () => {
    if (Array.isArray(value)) {
      return (
        <>
          {value.map((item, index) => (
            <Typography
              key={`${item}-${index}`}
              sx={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word'
              }}
            >
              {insertSoftBreaks(item)}
            </Typography>
          ))}
        </>
      );
    }

    return (
      <Typography
        sx={{
          whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word'
        }}
      >
        {insertSoftBreaks(value.toString())}
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        p: 1.5,
        overflow: 'hidden',
        minWidth: 180,
        maxWidth: 180,
        wordBreak: 'break-word'
      }}
    >
      <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
      {renderValue()}
    </Box>
  );
};

interface DetailsTabProps {
  factsheet: FactsheetDetails;
  isAuthenticated: boolean;
}

const DetailsTab = ({ factsheet, isAuthenticated }: DetailsTabProps) => {
  const dateFormat = useUserDateFormat();
  const { indexDetails, symbols, mapLocation, mapZoom } = factsheet;

  const startDate = useMemo(
    () => formatFactsheetDate(indexDetails['startDate'] as string, dateFormat.value),
    [dateFormat.value, indexDetails]
  );

  const endDate = useMemo(
    () =>
      formatFactsheetDateWithFallback(
        indexDetails['endDate'] as string | undefined,
        dateFormat.value,
        '(ongoing)'
      ),
    [dateFormat.value, indexDetails]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '24px' }}>
      <Box sx={{ flex: 1 }}>
        {/* Primary details section */}
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'flex-start'
          }}
        >
          <Tile
            label='Currency / Units'
            value={`${indexDetails['currency']} / ${indexDetails['units']}`}
          />
          <Tile
            label='Frequency'
            value={indexDetails['frequency']}
          />
          <Tile
            label='Pricing Basis'
            value={indexDetails['pricingBasis']}
          />
          <Tile
            label='Timezone'
            value={indexDetails['timeZone']}
          />
          <Tile
            label='Trading Hub'
            value={indexDetails['tradingHub']}
          />
          <Tile
            label='Delivery Basis'
            value={indexDetails['deliveryBasis']}
          />
          <Tile
            label='Commodity'
            value={indexDetails['commodity']}
          />
          <Tile
            label='Index Module(s)'
            value={indexDetails['indexModules']}
          />
          <Tile
            label='Holiday Calendar'
            value={indexDetails['holidayCalendar']}
          />
          <Tile
            label='Last Trade Calendar'
            value={indexDetails['lastTradeCalendar']}
          />
          <Tile
            label='Columns Available'
            value={indexDetails['columnsAvailable']}
          />
          <Tile
            label='Increment'
            value={indexDetails['increment']}
          />
        </Paper>

        <Divider sx={{ backgroundColor: 'secondary.main', pr: 4 }} />

        {/* Status and dates section */}
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'flex-start'
          }}
        >
          <Tile
            label='Status'
            value={indexDetails['status']}
          />
          <Tile
            label='Start Date'
            value={startDate}
          />
          <Tile
            label='End Date'
            value={endDate}
          />
        </Paper>

        <Divider sx={{ backgroundColor: 'secondary.main', pr: 4 }} />

        {/* Symbol information section */}
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'flex-start'
          }}
        >
          <Tile
            label='Spot Alias'
            value={indexDetails['spotAlias'] || '(none)'}
          />
          <Tile
            label='Forward Curve'
            value={indexDetails['forwardCurve'] ? 'Yes' : 'No'}
          />
          <Tile
            label='Num Items'
            value={symbols?.length.toString()}
          />
        </Paper>
      </Box>

      {isAuthenticated && mapLocation && (
        <MapImage
          location={mapLocation}
          zoom={mapZoom ?? '10'}
        />
      )}
    </Box>
  );
};

export default DetailsTab;
